import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { FC } from 'react';
import { useResponsiveType } from '../../hooks/useResponsiveType';
import { Close } from '../icon/icons/Close';
import { useBackgroundColor, getHighlightColor } from '../../utils';
import { LinkTo } from '../linkTo/LinkTo';

export interface TagProps {
  label: string;
  onClick?: any;
  id?: string;
  url?: string;
  small?: boolean;
  colorCode?: string; // Provide an overide for the colour instead of using the label
  isActive?: boolean;
}

interface StyledTagProps {
  colorCode: string;
  small?: boolean;
  isActive?: boolean;
}

export const StyledLinkTag = styled(LinkTo, {
  shouldForwardProp: (prop) => !['colorCode', 'small', 'isActive'].includes(prop),
})<StyledLinkProps>`
  ${({ theme: { space, media, colors }, colorCode, small, isActive }) => css`
    display: flex;
    align-items: center;
    gap: ${space.xSmall};
    width: max-content;
    padding: ${small
      ? `${space.xxSmall} ${space.small}`
      : `${space.fluidxxSmallToSmall} ${space.fluidSmall}`};
    border-radius: 13px;
    ${useBackgroundColor(getHighlightColor(colorCode, isActive ? '50' : '20'))};
    ${useResponsiveType(small ? 'capsSmall' : 'caps')};
    text-decoration: none;

    &:hover {
      ${useBackgroundColor(getHighlightColor(colorCode, '80'))};
    }

    &.is-active {
      ${useBackgroundColor(getHighlightColor(colorCode, '80'))};
    }

    @media (min-width: ${media.large}) {
      border-radius: ${small ? '13px' : '22px'};
    }
  `};
`;

export const StyledSpanTag = styled.span<StyledTagProps>`
  ${({ theme: { space, media }, colorCode, small, isActive }) => css`
    display: flex;
    align-items: center;
    gap: ${space.xSmall};
    width: max-content;
    padding: ${small
      ? `${space.xxSmall} ${space.small}`
      : `${space.fluidxxSmallToSmall} ${space.fluidSmall}`};
    border-radius: 13px;
    ${useBackgroundColor(
      getHighlightColor(colorCode, isActive ? '50' : '20', '20')
    )};
    ${useResponsiveType(small ? 'capsSmall' : 'caps')};
    text-decoration: none;

    @media (min-width: ${media.large}) {
      border-radius: ${small ? '13px' : '22px'};
    }
  `};
`;

const StyledButtonTag = styled.button<StyledTagProps>`
  ${({ theme: { space, media, colors }, colorCode, small, isActive }) => css`
    display: flex;
    align-items: center;
    gap: ${space.xSmall};
    width: max-content;
    padding: ${small
      ? `${space.xxSmall} ${space.small}`
      : `${space.fluidXxSmall} ${space.fluidSmall}`};
    border-radius: 32px;
    ${colorCode === 'clear' &&
    css`
      border: 2px solid ${colors.brandOne100};
    `}
    ${useBackgroundColor(getHighlightColor(colorCode, isActive ? '50' : '20'))};
    ${useResponsiveType(small ? 'capsSmall' : 'caps')};
    text-decoration: none;

    &:hover {
      ${useBackgroundColor(getHighlightColor(colorCode, '80'))};
    }

    @media (min-width: ${media.large}) {
      border-radius: ${small ? '13px' : '32px'};
    }
  `};
`;

export const Tag: FC<TagProps & Partial<HTMLElement>> = ({
  label,
  onClick,
  id,
  url,
  small,
  colorCode,
  isActive,
  ...rest
}) => {
  let as: 'button' | 'span' = 'span'; // Default to 'span'
  let type: 'button' | undefined;

  if (onClick) {
    as = 'button';
    type = 'button';
  }

  if (url) {
    return (
      <StyledLinkTag
        onClick={onClick}
        data-test="tag"
        id={id}
        colorCode={colorCode || label}
        label={label}
        to={url}
        small={small}
        isActive={isActive}
        {...rest}
      >
        {label}
      </StyledLinkTag>
    );
  }

  if (!url && onClick) {
    return (
      <StyledButtonTag
        onClick={onClick}
        type="button"
        data-test="tag"
        id={id}
        as="button"
        colorCode={colorCode || label}
        small={small}
        isActive={isActive}
        {...rest}
      >
        {label}
        {onClick && <Close size={colorCode === 'clear' ? 'small' : 'xSmall'} />}
      </StyledButtonTag>
    );
  }

  return (
    <StyledSpanTag
      id={id}
      as={as}
      colorCode={colorCode || label}
      small={small}
      isActive={isActive}
      data-test="tag"
      {...rest}
    >
      {label}
    </StyledSpanTag>
  );
};

export default Tag;
